'use client'

import * as React from 'react'
import { IconArrowRight } from '@repo/design-system-cf/components/icons'
import { runAfterWindowLoadIdle } from 'utils/dom'

const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainder = Math.floor(seconds % 60)
  return `${minutes}:${remainder.toString().padStart(2, '0')}`
}

interface SeedanceVideoItemProps {
  src: string
  tryItLabel: string
  /** Wrapper sizing. Defaults to the carousel item width; grids can pass `w-full`. */
  className?: string
  /** Tile aspect ratio. Defaults to the carousel's 3:4; grids can pass `aspect-[9/16]`. */
  aspectClassName?: string
  /** Show the video's real duration as a badge in the bottom-left corner. */
  showDuration?: boolean
  'data-card'?: true
}

export function SeedanceVideoItem({
  src,
  tryItLabel,
  className = 'shrink-0 w-[180px] lg:w-[210px] snap-center',
  aspectClassName = 'aspect-[3/4]',
  showDuration = false,
  ...rest
}: SeedanceVideoItemProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = React.useState(false)
  const [isPageVisible, setIsPageVisible] = React.useState(true)
  const [duration, setDuration] = React.useState<number | null>(null)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
        } else {
          setIsInView(false)
        }
      },
      { threshold: 0, rootMargin: '50px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Stop decoding when the tab is backgrounded: IntersectionObserver still reports the tile as
  // in-view, so without this a hidden tab keeps every visible video playing.
  React.useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden)
    handleVisibilityChange()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (isInView && isPageVisible) {
      return runAfterWindowLoadIdle(() => {
        video.preload = 'auto'
        void video.play().catch(() => {})
      })
    }

    video.pause()
    // Rewind only when scrolled out of view; a backgrounded tab keeps its position.
    if (!isInView) {
      video.currentTime = 0
    }
  }, [isInView, isPageVisible])

  return (
    <div ref={containerRef} className={className} {...rest}>
      <a
        href={`${STUDIO_BASE_URL}/ai-video-generator?model=seedance_v2_5`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tryItLabel}
        className={`group relative block ${aspectClassName} w-full overflow-hidden rounded-sm border border-cf-sf-gray-350/40 bg-cf-grey-200 focus-visible:ring-2 focus-visible:ring-cf-green focus-visible:outline-none`}
      >
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="none"
          className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
          onError={e => e.stopPropagation()}
          onLoadedMetadata={e => {
            const value = e.currentTarget.duration
            if (Number.isFinite(value) && value > 0) {
              setDuration(value)
            }
          }}
        />
        {showDuration && duration !== null && (
          <span className="pointer-events-none absolute bottom-1.5 left-1.5 inline-flex items-center gap-0.5 rounded bg-cf-black/60 py-0.5 pr-1.5 pl-1 text-[11px] font-medium text-cf-white backdrop-blur-sm">
            <IconArrowRight className="size-4" />
            {formatDuration(duration)}
          </span>
        )}
      </a>
    </div>
  )
}
